/* ==========================================================================
   Freight HHG — theme.js
   Vanilla, dependency-free. ES2017+ (Shopify checkout-supported browsers).
   Keep small: defer-loaded, no framework runtime.
   ========================================================================== */

(function () {
  'use strict';

  const $ = (sel, ctx = document) => ctx.querySelector(sel);
  const $$ = (sel, ctx = document) => Array.from(ctx.querySelectorAll(sel));

  const FOCUSABLE = 'a[href], button:not([disabled]), input:not([disabled]), select:not([disabled]), textarea:not([disabled]), [tabindex]:not([tabindex="-1"])';

  const debounce = (fn, ms = 200) => {
    let t;
    return (...args) => { clearTimeout(t); t = setTimeout(() => fn.apply(null, args), ms); };
  };

  const decodeEntities = (str) => {
    const el = document.createElement('textarea');
    el.innerHTML = str;
    return el.value;
  };
  // shop.money_format may contain HTML entities (e.g. &pound;); decode so the result
  // is correct whether it's later assigned via textContent or innerHTML.
  const MONEY_FORMAT = decodeEntities(window.theme?.moneyFormat || '£{{amount}}');

  const formatMoney = (cents) => {
    const n = cents / 100;
    return MONEY_FORMAT
      .replace('{{amount_no_decimals_with_comma_separator}}', Math.round(n).toString().replace(/\B(?=(\d{3})+(?!\d))/g, ','))
      .replace('{{amount_with_comma_separator}}', n.toFixed(2).replace('.', ','))
      .replace('{{amount_no_decimals}}', Math.round(n).toString())
      .replace('{{amount}}', n.toFixed(2));
  };

  /* ------------------------------------------------------------------ */
  /*  Body scroll lock + open/close helpers                              */
  /* ------------------------------------------------------------------ */
  let scrollLockCount = 0;
  const lockScroll = () => { if (scrollLockCount++ === 0) document.body.classList.add('scroll-locked'); };
  const unlockScroll = () => { scrollLockCount = Math.max(0, scrollLockCount - 1); if (scrollLockCount === 0) document.body.classList.remove('scroll-locked'); };

  function trapFocus(container) {
    const focusables = $$(FOCUSABLE, container).filter(el => el.offsetParent !== null);
    if (!focusables.length) return () => {};
    const first = focusables[0]; const last = focusables[focusables.length - 1];
    const onKey = (e) => {
      if (e.key !== 'Tab') return;
      if (e.shiftKey && document.activeElement === first) { e.preventDefault(); last.focus(); }
      else if (!e.shiftKey && document.activeElement === last) { e.preventDefault(); first.focus(); }
    };
    container.addEventListener('keydown', onKey);
    return () => container.removeEventListener('keydown', onKey);
  }

  /* ------------------------------------------------------------------ */
  /*  Generic toggleable overlay                                         */
  /* ------------------------------------------------------------------ */
  class Overlay {
    constructor(el) {
      this.el = el;
      this.releaseFocus = null;
      this.lastFocused = null;
      el.addEventListener('click', (e) => {
        if (e.target.matches('[data-overlay-close]') || e.target.closest('[data-overlay-close]')) this.close();
        if (e.target === el.querySelector('[data-overlay-backdrop]')) this.close();
      });
      document.addEventListener('keydown', (e) => {
        if (e.key === 'Escape' && el.classList.contains('is-open')) this.close();
      });
    }
    open() {
      this.lastFocused = document.activeElement;
      this.el.classList.add('is-open');
      this.el.setAttribute('aria-hidden', 'false');
      lockScroll();
      requestAnimationFrame(() => {
        const focusTarget = this.el.querySelector('[data-overlay-focus]') || this.el.querySelector(FOCUSABLE);
        focusTarget?.focus();
        this.releaseFocus = trapFocus(this.el);
      });
      this.el.dispatchEvent(new CustomEvent('overlay:open'));
    }
    close() {
      this.el.classList.remove('is-open');
      this.el.setAttribute('aria-hidden', 'true');
      unlockScroll();
      if (this.releaseFocus) this.releaseFocus();
      this.lastFocused?.focus?.();
      this.el.dispatchEvent(new CustomEvent('overlay:close'));
    }
    toggle() { this.el.classList.contains('is-open') ? this.close() : this.open(); }
  }

  const overlays = {};
  function registerOverlay(key, selector) {
    const el = $(selector);
    if (el) overlays[key] = new Overlay(el);
  }

  /* ------------------------------------------------------------------ */
  /*  Sidebar nav — animated accordion                                  */
  /* ------------------------------------------------------------------ */
  function initNavAccordion() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-nav-toggle]');
      if (!trigger) return;
      const item = trigger.closest('.nav-item');
      if (!item) return;
      const open = item.getAttribute('aria-expanded') === 'true';
      if (!open) {
        item.closest('ul').querySelectorAll('.nav-item[aria-expanded="true"]').forEach(other => {
          if (other === item) return;
          other.setAttribute('aria-expanded', 'false');
          const t = other.querySelector('[data-nav-toggle]');
          if (t) t.setAttribute('aria-expanded', 'false');
        });
      }
      item.setAttribute('aria-expanded', open ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', open ? 'false' : 'true');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Mobile drawer                                                     */
  /* ------------------------------------------------------------------ */
  function initMobileDrawer() {
    const drawer = $('#MobileDrawer');
    if (!drawer) return;
    overlays.mobileDrawer = new Overlay(drawer);
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-mobile-menu-open]')) { e.preventDefault(); overlays.mobileDrawer.open(); }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Search overlay + predictive search                                */
  /* ------------------------------------------------------------------ */
  function initSearch() {
    const overlay = $('#SearchOverlay');
    if (!overlay) return;
    overlays.search = new Overlay(overlay);

    const input = $('#SearchInput', overlay);
    const results = $('[data-search-results]', overlay);
    const viewAll = $('[data-search-viewall]', overlay);
    const empty = $('[data-search-empty]', overlay);
    const loading = $('[data-search-loading]', overlay);
    const form = $('[data-search-form]', overlay);
    const panel = $('[data-search-panel]', overlay);

    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-search-open]')) {
        e.preventDefault();
        overlays.search.open();
      }
    });

    overlay.addEventListener('overlay:open', () => {
      input.value = '';
      results.innerHTML = '';
      viewAll.hidden = true; empty.hidden = true; loading.hidden = true;
      if (panel) panel.hidden = true;
    });

    overlay.addEventListener('overlay:close', () => {
      results.innerHTML = '';
      viewAll.hidden = true; empty.hidden = true; loading.hidden = true;
      if (panel) panel.hidden = true;
    });

    const search = debounce(async (q) => {
      if (q.length < 3) {
        results.innerHTML = ''; viewAll.hidden = true; empty.hidden = true; loading.hidden = true;
        if (panel) panel.hidden = true;
        return;
      }
      loading.hidden = false; empty.hidden = true;
      if (panel) panel.hidden = false;
      try {
        const url = `/search/suggest.json?q=${encodeURIComponent(q)}&resources[type]=product&resources[limit]=8&resources[options][unavailable_products]=last`;
        const res = await fetch(url, { headers: { 'Accept': 'application/json' } });
        const data = await res.json();
        const products = data.resources?.results?.products || [];
        loading.hidden = true;
        if (!products.length) {
          results.innerHTML = '';
          empty.hidden = false;
          empty.textContent = `No products found for "${q}".`;
          viewAll.hidden = true;
          return;
        }
        results.innerHTML = products.map(p => productResultHTML(p)).join('');
        viewAll.hidden = false;
        const viewAllLink = viewAll.querySelector('[data-search-viewall-href]');
        const viewAllLabel = viewAll.querySelector('[data-search-viewall-label]');
        if (viewAllLink) viewAllLink.href = `/search?q=${encodeURIComponent(q)}&type=product`;
        if (viewAllLabel) {
          viewAllLabel.textContent = `View all results for "${q}"`;
          fetch(`/search?q=${encodeURIComponent(q)}&type=product&section_id=search-count`)
            .then(r => r.text())
            .then(text => {
              const n = parseInt(text.trim());
              if (!isNaN(n)) viewAllLabel.textContent = `View all ${n} results for "${q}"`;
            })
            .catch(() => {});
        }
      } catch (err) {
        console.warn('Search failed', err);
        loading.hidden = true;
      }
    }, 200);

    input?.addEventListener('input', (e) => search(e.target.value.trim()));
    form?.addEventListener('submit', () => { /* let it submit to /search */ });
  }

  function productResultHTML(p) {
    const imgSrc = p.image ? p.image.replace(/(\.\w+)$/, '_79x100_crop_center$1') : '';
    const img = imgSrc
      ? `<img class="search-result__img" src="${imgSrc}" alt="${escapeHTML(p.title)}" width="79" height="100" loading="lazy">`
      : `<div class="search-result__img"></div>`;
    const toCents = s => Math.round(parseFloat(s) * 100);
    let price = '';
    if (p.price_min != null) {
      const min = toCents(p.price_min);
      const max = p.price_max != null ? toCents(p.price_max) : min;
      price = (max > min ? '<span class="price__from">From</span> ' : '') + formatMoney(min);
    } else if (p.price) {
      price = formatMoney(toCents(p.price));
    }
    return `
      <a class="search-result" href="${p.url}">
        ${img}
        <div class="search-result__body">
          <span class="search-result__title">${escapeHTML(p.title)}</span>
          ${price ? `<span class="search-result__price">${price}</span>` : ''}
        </div>
      </a>`;
  }

  function escapeHTML(s) {
    return String(s ?? '').replace(/[&<>"']/g, c => ({ '&':'&amp;','<':'&lt;','>':'&gt;','"':'&quot;',"'":'&#39;' }[c]));
  }

  /* ------------------------------------------------------------------ */
  /*  Cart drawer                                                       */
  /* ------------------------------------------------------------------ */
  const Cart = {
    drawer: null,
    init() {
      this.drawer = $('#CartDrawer');
      if (this.drawer) overlays.cart = new Overlay(this.drawer);

      document.addEventListener('click', (e) => {
        const open = e.target.closest('[data-cart-open]');
        if (open) { e.preventDefault(); this.refresh().then(() => overlays.cart?.open()); return; }

        const remove = e.target.closest('[data-cart-remove]');
        if (remove) { e.preventDefault(); this.update(remove.dataset.cartRemove, 0); return; }

        const minus = e.target.closest('[data-cart-minus]');
        if (minus) { this.changeQty(minus.dataset.cartMinus, -1); return; }
        const plus = e.target.closest('[data-cart-plus]');
        if (plus) { this.changeQty(plus.dataset.cartPlus, 1); return; }
      });

      document.addEventListener('change', (e) => {
        const noteField = e.target.closest('[data-cart-note]');
        if (!noteField) return;
        fetch('/cart/update.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ note: noteField.value })
        }).catch(() => {});
      });

      // Forms with [data-product-form] auto-add to cart
      document.addEventListener('submit', async (e) => {
        const form = e.target.closest('form[data-product-form]');
        if (!form) return;
        e.preventDefault();
        const btn = form.querySelector('[type="submit"]');
        const original = btn?.textContent;
        if (btn) { btn.disabled = true; btn.textContent = '…'; }
        try {
          const fd = new FormData(form);
          const res = await fetch('/cart/add.js', { method: 'POST', body: fd, headers: { 'Accept': 'application/json' } });
          if (!res.ok) throw new Error('Add to cart failed');
          await this.refresh();
          overlays.cart?.open();
        } catch (err) {
          console.warn(err);
          alert('Could not add to cart. Please try again.');
        } finally {
          if (btn) { btn.disabled = false; btn.textContent = original; }
        }
      });
    },
    async refresh() {
      try {
        const res = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
        const cart = await res.json();
        this.render(cart);
        this.updateCount(cart.item_count);
      } catch (err) { console.warn(err); }
    },
    async changeQty(key, delta) {
      const row = this.drawer?.querySelector(`[data-cart-row="${key}"]`);
      const input = row?.querySelector('input[data-cart-qty]');
      const newQty = Math.max(0, parseInt(input?.value || '1') + delta);
      this.update(key, newQty);
    },
    async update(key, quantity) {
      try {
        const res = await fetch('/cart/change.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Accept': 'application/json' },
          body: JSON.stringify({ id: key, quantity })
        });
        const cart = await res.json();
        this.render(cart);
        this.updateCount(cart.item_count);
      } catch (err) { console.warn(err); }
    },
    render(cart) {
      const body = $('[data-cart-body]', this.drawer);
      const foot = $('[data-cart-foot]', this.drawer);
      if (!body) return;
      if (!cart.item_count) {
        body.innerHTML = `<div class="cart-drawer__empty"><p>Your cart is empty</p><a class="btn btn--outline btn--small" href="/collections/all" data-overlay-close>Continue shopping</a></div>`;
        if (foot) foot.hidden = true;
        return;
      }
      if (foot) foot.hidden = false;
      body.innerHTML = `
        <div class="cart-drawer__items">
          ${cart.items.map(item => this.itemHTML(item)).join('')}
        </div>`;
      const subtotalEl = $('[data-cart-subtotal]', this.drawer);
      if (subtotalEl) subtotalEl.innerHTML = formatMoney(cart.total_price);
    },
    itemHTML(item) {
      const img = item.image ? `<img src="${item.image}" alt="${escapeHTML(item.product_title)}" width="80" height="106" loading="lazy">` : '';
      const opts = item.variant_title && item.variant_title !== 'Default Title' ? `<div class="cart-drawer__item-meta">${escapeHTML(item.variant_title)}</div>` : '';
      return `
        <div class="cart-drawer__item" data-cart-row="${item.key}">
          <div class="cart-drawer__item-img">${img}</div>
          <div class="cart-drawer__item-info">
            <div class="cart-drawer__item-title"><a href="${item.url}">${escapeHTML(item.product_title)}</a></div>
            ${opts}
            <div class="cart-drawer__item-price">${formatMoney(item.final_line_price)}</div>
            <div class="cart-drawer__item-actions">
              <div class="qty">
                <button class="qty__btn" type="button" data-cart-minus="${item.key}" aria-label="Decrease">−</button>
                <input class="qty__num" type="text" data-cart-qty value="${item.quantity}" readonly />
                <button class="qty__btn" type="button" data-cart-plus="${item.key}" aria-label="Increase">+</button>
              </div>
              <button class="cart-drawer__remove" type="button" data-cart-remove="${item.key}">Remove</button>
            </div>
          </div>
        </div>`;
    },
    updateCount(n) {
      $$('[data-cart-count]').forEach(el => {
        el.textContent = n;
        el.hidden = n === 0;
      });
    }
  };

  /* ------------------------------------------------------------------ */
  /*  Accordion (product page, etc.)                                    */
  /* ------------------------------------------------------------------ */
  function initAccordions() {
    document.addEventListener('click', (e) => {
      const trigger = e.target.closest('[data-accordion-trigger]');
      if (!trigger) return;
      const item = trigger.closest('[data-accordion-item]');
      if (!item) return;
      const expanded = item.getAttribute('aria-expanded') === 'true';
      const exclusiveParent = item.closest('[data-accordion-exclusive]');
      if (exclusiveParent && !expanded) {
        exclusiveParent.querySelectorAll('[data-accordion-item][aria-expanded="true"]').forEach(open => {
          open.setAttribute('aria-expanded', 'false');
          open.querySelector('[data-accordion-trigger]')?.setAttribute('aria-expanded', 'false');
        });
      }
      item.setAttribute('aria-expanded', expanded ? 'false' : 'true');
      trigger.setAttribute('aria-expanded', expanded ? 'false' : 'true');
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Product recommendations (Search & Discovery + collection fallback) */
  /* ------------------------------------------------------------------ */
  function initProductRecommendations() {
    document.querySelectorAll('[data-recommendations-url]').forEach(container => {
      fetch(container.dataset.recommendationsUrl)
        .then(r => r.text())
        .then(html => {
          const doc = new DOMParser().parseFromString(html, 'text/html');
          const updated = doc.getElementById(container.id);
          if (updated && updated.querySelector('.product-card')) {
            container.innerHTML = updated.innerHTML;
            try{iWish&&setTimeout(()=>iWish.checkIwishColl(),2000)}catch(e){}
            initCarousels();
          }
        })
        .catch(() => {});
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Carousels (collection list, featured products)                    */
  /* ------------------------------------------------------------------ */
  function initCarousels() {
    document.querySelectorAll('[data-carousel]:not([data-carousel-init])').forEach(carousel => {
      carousel.setAttribute('data-carousel-init', '');
      const track = carousel.querySelector('[data-carousel-track]');
      if (!track) return;

      // Build [clones][originals][clones] so scrolling loops seamlessly in both directions
      const originals = [...track.children];
      const N = originals.length;
      const frag = document.createDocumentFragment();
      originals.forEach(el => frag.appendChild(el.cloneNode(true)));
      track.prepend(frag);
      originals.forEach(el => track.appendChild(el.cloneNode(true)));

      const cardWidth = () => {
        const c = track.firstElementChild;
        return c ? c.offsetWidth + (parseInt(getComputedStyle(track).gap) || 0) : 300;
      };
      const origWidth = () => cardWidth() * N;

      // Start at the real items (after the front clones)
      requestAnimationFrame(() => { track.scrollLeft = origWidth(); });

      // Hide arrows when there aren't enough items to scroll
      if (N <= 4) {
        carousel.querySelectorAll('.collection-carousel__btn').forEach(btn => btn.hidden = true);
      }

      // Set arrow vertical position to the centre of the card image
      requestAnimationFrame(() => {
        const card = track.children[N]; // first real card
        const img = card?.querySelector('.product-card__media, .collection-card__media, .article-card__media');
        if (img) {
          const updateArrowTop = () => {
            const h = img.offsetHeight;
            if (h > 0) carousel.style.setProperty('--carousel-arrow-top', `${h / 2}px`);
          };
          updateArrowTop();
          window.addEventListener('resize', updateArrowTop, { passive: true });
        }
      });

      // Silently reposition into the real zone whenever scroll enters a clone zone
      const reposition = () => {
        const ow = origWidth();
        if (track.scrollLeft < ow) {
          track.scrollLeft = track.scrollLeft + ow;
        } else if (track.scrollLeft >= 2 * ow) {
          track.scrollLeft = track.scrollLeft - ow;
        }
      };
      track.addEventListener('scrollend', reposition);
      let debounceTimer;
      track.addEventListener('scroll', () => {
        clearTimeout(debounceTimer);
        debounceTimer = setTimeout(reposition, 200);
      }, { passive: true });

      // Button clicks
      carousel.querySelector('[data-carousel-prev]')?.addEventListener('click', () => {
        track.scrollBy({ left: -cardWidth(), behavior: 'smooth' });
      });
      carousel.querySelector('[data-carousel-next]')?.addEventListener('click', () => {
        track.scrollBy({ left: cardWidth(), behavior: 'smooth' });
      });

      // Mouse drag to scroll (touch handles natively)
      track.style.cursor = 'grab';
      track.addEventListener('mousedown', e => {
        const startX = e.pageX;
        const scrollStart = track.scrollLeft;
        let moved = false;
        track.style.cursor = 'grabbing';
        track.style.userSelect = 'none';
        track.style.scrollSnapType = 'none';

        const onMove = e => {
          const dx = startX - e.pageX;
          if (Math.abs(dx) > 4) moved = true;
          track.scrollLeft = scrollStart + dx;
        };

        const onUp = () => {
          window.removeEventListener('mousemove', onMove);
          window.removeEventListener('mouseup', onUp);
          track.style.cursor = 'grab';
          track.style.userSelect = '';
          track.style.scrollSnapType = '';
          const cw = cardWidth();
          track.scrollTo({ left: Math.round(track.scrollLeft / cw) * cw, behavior: 'smooth' });
          if (moved) {
            track.addEventListener('click', ev => {
              ev.preventDefault();
              ev.stopPropagation();
            }, { capture: true, once: true });
          }
        };

        window.addEventListener('mousemove', onMove);
        window.addEventListener('mouseup', onUp);
      });
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Modal opener (size guide, etc.)                                   */
  /* ------------------------------------------------------------------ */
  function initModals() {
    const modal = $('#ModalOverlay');
    if (modal) overlays.modal = new Overlay(modal);

    document.addEventListener('click', (e) => {
      const opener = e.target.closest('[data-modal-open]');
      if (!opener) return;
      e.preventDefault();
      const titleEl = opener.dataset.modalTitle ? opener.dataset.modalTitle : '';
      const contentSel = opener.dataset.modalContent;
      const titleNode = $('[data-modal-title]', modal);
      const bodyNode = $('[data-modal-body]', modal);
      if (titleNode) titleNode.textContent = titleEl;
      if (bodyNode && contentSel) {
        const src = $(contentSel);
        bodyNode.innerHTML = src ? src.innerHTML : '';
      }
      overlays.modal?.open();
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Currency overlay                                                  */
  /* ------------------------------------------------------------------ */
  function initCurrency() {
    const overlay = $('#CurrencyOverlay');
    if (!overlay) return;
    overlays.currency = new Overlay(overlay);
    document.addEventListener('click', (e) => {
      if (e.target.closest('[data-currency-open]')) { e.preventDefault(); overlays.currency.open(); }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Product page                                                      */
  /* ------------------------------------------------------------------ */
  function initProduct() {
    const root = $('[data-product]');
    if (!root) return;
    const productJSON = $('[data-product-json]', root);
    if (!productJSON) return;
    let product;
    try { product = JSON.parse(productJSON.textContent); } catch { return; }

    const variantInput = $('[name="id"]', root);
    const priceEl = $('[data-product-price]', root);
    const stickyTitle = $('[data-sticky-title]');
    const stickyMeta = $('[data-sticky-meta]');
    const stickyBtn = $('[data-sticky-atc-btn]');
    const stickySentinel = $('[data-sticky-sentinel]', root);
    const atcBtn = $('[data-product-atc]', root);
    const optionInputs = $$('[data-option-input]', root);
    const labelEls = $$('[data-option-label]', root);
    const panels = $$('[data-gallery-panel]', root);
    const mediaMapEl = $('[data-media-map]', root);
    let mediaMap = {};
    try { if (mediaMapEl) mediaMap = JSON.parse(mediaMapEl.textContent); } catch {}

    const thumbEls = $$('[data-thumb-media-id]', root);
    let galleryIdx = 0;

    function galleryGoTo(idx, animated) {
      const n = panels.length;
      galleryIdx = ((idx % n) + n) % n; // wrap so the gallery loops in both directions
      const dur = animated ? '0.32s' : '0s';
      panels.forEach((p, i) => {
        p.style.transition = `transform ${dur} ease`;
        p.style.transform = `translateX(${(i - galleryIdx) * 100}%)`;
        const vid = p.querySelector('video');
        if (vid) i === galleryIdx ? vid.play().catch(() => {}) : vid.pause();
        p.classList.toggle('is-current', i === galleryIdx); // sizes the box in natural-aspect mode
      });
      thumbEls.forEach((t, i) => t.classList.toggle('is-active', i === galleryIdx));
    }

    function activatePanel(mediaId) {
      const idx = panels.findIndex(p => p.dataset.mediaId === String(mediaId));
      if (idx !== -1) galleryGoTo(idx, true);
    }

    // Init gallery positions (no animation)
    {
      const initIdx = panels.findIndex(p => p.classList.contains('is-active'));
      galleryGoTo(initIdx !== -1 ? initIdx : 0, false);
    }

    function currentOptions() {
      return optionInputs.reduce((acc, el) => {
        const idx = parseInt(el.dataset.optionInput);
        if (el.checked || (el.tagName === 'BUTTON' && el.classList.contains('is-active'))) {
          acc[idx] = el.value || el.dataset.value;
        }
        return acc;
      }, {});
    }

    function findVariant(opts) {
      return product.variants.find(v => {
        return [v.option1, v.option2, v.option3].every((val, i) => {
          if (opts[i] == null) return true;
          return val === opts[i];
        });
      });
    }

    function update() {
      const opts = currentOptions();
      labelEls.forEach(el => {
        const idx = parseInt(el.dataset.optionLabel);
        if (opts[idx]) el.textContent = opts[idx];
      });
      const v = findVariant(opts);
      if (!v) return;
      variantInput.value = v.id;
      // Let storefront apps (e.g. the back-in-stock app) react to a client-side
      // variant switch so they can swap in their notify UI for sold-out variants.
      variantInput.dispatchEvent(new Event('change', { bubbles: true }));
      if (priceEl) priceEl.innerHTML = renderPrice(v);
      if (atcBtn) {
        atcBtn.disabled = !v.available;
        atcBtn.textContent = v.available ? (atcBtn.dataset.labelAdd || 'Add to cart') : (atcBtn.dataset.labelSoldout || 'Sold out');
      }
      if (stickyBtn) {
        stickyBtn.disabled = !v.available;
        stickyBtn.classList.toggle('is-soldout', !v.available);
        stickyBtn.textContent = v.available ? (stickyBtn.dataset.labelAdd || 'Add to cart') : (stickyBtn.dataset.labelSoldout || 'Sold out');
      }
      if (stickyTitle) stickyTitle.textContent = product.title;
      // Single-variant products have a synthetic "Default Title" variant and no
      // variant selector — omit the variant name (and separator) in the sticky bar.
      if (stickyMeta) stickyMeta.textContent = product.variants.length > 1
        ? `${formatMoney(v.price)} — ${v.title}`
        : formatMoney(v.price);
      const mediaId = mediaMap[v.id];
      if (mediaId) activatePanel(mediaId);
      // URL update
      try {
        const url = new URL(window.location.href);
        url.searchParams.set('variant', v.id);
        window.history.replaceState({}, '', url);
      } catch {}
    }

    function renderPrice(v) {
      if (v.compare_at_price && v.compare_at_price > v.price) {
        return `<s>${formatMoney(v.compare_at_price)}</s> ${formatMoney(v.price)}`;
      }
      return formatMoney(v.price);
    }

    optionInputs.forEach(el => {
      el.addEventListener('change', update);
      if (el.tagName === 'BUTTON') {
        el.addEventListener('click', (e) => {
          // Sold-out options stay selectable so the back-in-stock app can show its
          // notify button for the chosen variant; they keep the .is-soldout styling.
          const idx = el.dataset.optionInput;
          $$(`[data-option-input="${idx}"]`, root).forEach(b => b.classList.remove('is-active'));
          el.classList.add('is-active');
          update();
        });
      }
      if (el.classList?.contains('swatch')) {
        el.addEventListener('click', () => {
          // Sold-out swatches stay selectable (back-in-stock notify flow); they keep
          // the .swatch--soldout diagonal-strike styling.
          const idx = el.dataset.optionInput;
          $$(`[data-option-input="${idx}"]`, root).forEach(b => b.setAttribute('aria-checked', 'false'));
          el.setAttribute('aria-checked', 'true');
          update();
        });
      }
    });

    // Quantity
    $$('[data-qty-minus]', root).forEach(b => b.addEventListener('click', () => {
      const i = $('[data-qty-input]', root); i.value = Math.max(1, parseInt(i.value) - 1);
    }));
    $$('[data-qty-plus]', root).forEach(b => b.addEventListener('click', () => {
      const i = $('[data-qty-input]', root); i.value = parseInt(i.value) + 1;
    }));

    // Gallery thumbs
    thumbEls.forEach((thumb, i) => {
      thumb.addEventListener('click', () => galleryGoTo(i, true));
    });

    // Gallery arrows — stop pointerdown bubbling so the drag handler on
    // .product-gallery__main doesn't capture the pointer and swallow the click
    const galleryPrev = root.querySelector('[data-gallery-prev]');
    const galleryNext = root.querySelector('[data-gallery-next]');
    [galleryPrev, galleryNext].forEach(btn => btn?.addEventListener('pointerdown', e => e.stopPropagation()));
    galleryPrev?.addEventListener('click', () => galleryGoTo(galleryIdx - 1, true));
    galleryNext?.addEventListener('click', () => galleryGoTo(galleryIdx + 1, true));

    // Gallery drag
    const galleryMain = root.querySelector('.product-gallery__main');
    if (galleryMain && panels.length > 1) {
      let startX = 0, isDragging = false;
      galleryMain.addEventListener('pointerdown', e => {
        e.preventDefault();
        startX = e.clientX; isDragging = true;
        galleryMain.setPointerCapture(e.pointerId);
        panels.forEach(p => p.style.transition = 'none');
      });
      galleryMain.addEventListener('pointermove', e => {
        if (!isDragging) return;
        const pct = ((e.clientX - startX) / galleryMain.offsetWidth) * 100;
        panels.forEach((p, i) => {
          p.style.transform = `translateX(${(i - galleryIdx) * 100 + pct}%)`;
        });
      });
      galleryMain.addEventListener('pointerup', e => {
        if (!isDragging) return;
        isDragging = false;
        const diff = e.clientX - startX;
        const next = Math.abs(diff) >= 40
          ? (diff < 0 ? galleryIdx + 1 : galleryIdx - 1) // galleryGoTo wraps
          : galleryIdx;
        galleryGoTo(next, true);
      });
      galleryMain.addEventListener('pointercancel', () => {
        if (isDragging) { isDragging = false; galleryGoTo(galleryIdx, true); }
      });
    }

    // Sticky ATC — mirrors the real ATC: submit when in stock, inert "Sold out" when not.
    // (A sold-out variant doesn't add to cart; matches the disabled main button.)
    stickyBtn?.addEventListener('click', () => {
      if (stickyBtn.disabled) return;
      atcBtn?.click();
    });

    // Sticky ATC visibility — observe a fixed sentinel rather than the ATC button, which a
    // back-in-stock app may hide for sold-out variants (a hidden element reads as
    // not-intersecting and would wrongly force the bar visible).
    const sticky = $('[data-sticky-atc]');
    const stickyTarget = stickySentinel || atcBtn;
    if (sticky && stickyTarget && 'IntersectionObserver' in window) {
      const io = new IntersectionObserver(([entry]) => {
        sticky.classList.toggle('is-visible', !entry.isIntersecting);
      }, { threshold: 0, rootMargin: '0px 0px -20% 0px' });
      io.observe(stickyTarget);
    }

    update();
  }

  /* ------------------------------------------------------------------ */
  /*  Slideshow                                                         */
  /* ------------------------------------------------------------------ */
  function initSlideshow() {
    document.querySelectorAll('[data-slideshow]').forEach(el => {
      const track = el.querySelector('[data-slideshow-track]');
      if (!track) return;

      const slides = track.querySelectorAll('[data-slide]');
      const count = slides.length;

      // Only ever load/play slideshow video on desktop and on a capable,
      // non-metered connection. Mobile and Save-Data/2g/3g keep the still image,
      // so 4G-on-the-go never downloads the video.
      const conn = navigator.connection || navigator.mozConnection || navigator.webkitConnection;
      const dataSaver = conn && (conn.saveData || /(^|-)(slow-2g|2g|3g)$/.test(conn.effectiveType || ''));
      const reducedData = window.matchMedia('(prefers-reduced-data: reduce)').matches;
      const isDesktop = window.matchMedia('(min-width: 750px)').matches;
      const allowVideo = isDesktop && !dataSaver && !reducedData;

      function playSlideVideo(index) {
        slides.forEach((slide, i) => {
          const video = slide.querySelector('.slideshow__video');
          if (!video) return;
          if (i === index && allowVideo) {
            if (!video.dataset.loaded) {
              video.querySelectorAll('source[data-src]').forEach(s => { s.src = s.dataset.src; });
              video.dataset.loaded = '1';
              video.load();
            }
            video.currentTime = 0;
            video.play().then(() => video.classList.add('is-playing')).catch(() => {});
          } else {
            video.pause();
            video.classList.remove('is-playing');
          }
        });
      }

      // Start the first slide's video (no-op on mobile / constrained connections)
      playSlideVideo(0);

      if (count <= 1) return;

      let current = 0;
      function goTo(index) {
        current = (index + count) % count;
        track.style.transform = `translateX(-${current * 100}%)`;
        el.querySelectorAll('[data-slide-caption]').forEach(cap => {
          const i = parseInt(cap.dataset.slideCaption);
          const active = i === current;
          cap.classList.toggle('is-active', active);
          cap.setAttribute('aria-hidden', active ? 'false' : 'true');
          if (active) el.classList.toggle('slideshow--light', 'captionLight' in cap.dataset);
        });
        playSlideVideo(current);
      }
      el.querySelector('[data-prev]')?.addEventListener('click', () => goTo(current - 1));
      el.querySelector('[data-next]')?.addEventListener('click', () => goTo(current + 1));
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Collection filters (AJAX tag + sort)                              */
  /* ------------------------------------------------------------------ */
  function initCollectionFilters() {
    const grid = document.querySelector('[data-collection-grid]');
    if (!grid) return;

    const baseUrl = grid.dataset.baseUrl;
    const sortSelect = grid.querySelector('[data-filter-sort]');
    const paginationEl = document.querySelector('[data-collection-pagination]');
    function buildUrl() {
      const sort = sortSelect ? sortSelect.value : '';
      let url = baseUrl;
      if (sort) url += '?sort_by=' + encodeURIComponent(sort);
      return url;
    }

    async function loadPage(url, append) {
      try {
        try{iWish&&setTimeout(()=>iWish.checkIwishColl(),2000)}catch(e){}
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const newGrid = doc.querySelector('[data-collection-grid]');
        if (!append) {
          grid.querySelectorAll('.product-card, .collection-grid__empty').forEach(el => el.remove());
        }
        if (newGrid) {
          newGrid.querySelectorAll('.product-card, .collection-grid__empty').forEach(el => grid.appendChild(el));
        }
        const newPagination = doc.querySelector('[data-collection-pagination]');
        if (paginationEl) paginationEl.innerHTML = newPagination ? newPagination.innerHTML : '';
        if (!append) window.history.pushState({}, '', url);
      } catch (e) {
        // silent — page still functions without AJAX
      }
    }

    async function applyFilters() {
      grid.querySelectorAll('.product-card').forEach(el => { el.style.opacity = '0.4'; });
      try {
        await loadPage(buildUrl(), false);
      } finally {
        grid.querySelectorAll('.product-card').forEach(el => { el.style.opacity = ''; });
      }
    }

    if (sortSelect) sortSelect.addEventListener('change', applyFilters);

    if (paginationEl) {
      paginationEl.addEventListener('click', async function (e) {
        const btn = e.target.closest('[data-load-more]');
        if (!btn) return;
        e.preventDefault();
        const url = btn.dataset.loadMore;
        btn.disabled = true;
        btn.textContent = 'Loading…';
        await loadPage(url, true);
      });
    }
  }

  /* ------------------------------------------------------------------ */
  /*  Search load more                                                  */
  /* ------------------------------------------------------------------ */
  function initSearchLoadMore() {
    const grid = document.querySelector('[data-search-grid]');
    const paginationEl = document.querySelector('[data-search-pagination]');
    if (!grid || !paginationEl) return;

    paginationEl.addEventListener('click', async function (e) {
      const btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      e.preventDefault();
      const url = btn.dataset.loadMore;
      btn.disabled = true;
      btn.textContent = 'Loading…';
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const newGrid = doc.querySelector('[data-search-grid]');
        if (newGrid) {
          newGrid.querySelectorAll('.product-card').forEach(el => grid.appendChild(el));
        }
        const newPagination = doc.querySelector('[data-search-pagination]');
        paginationEl.innerHTML = newPagination ? newPagination.innerHTML : '';
      } catch (e) {
        btn.disabled = false;
        btn.textContent = 'Load more';
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Blog load more                                                    */
  /* ------------------------------------------------------------------ */
  function initBlogLoadMore() {
    const grid = document.querySelector('[data-article-grid]');
    const paginationEl = document.querySelector('[data-blog-pagination]');
    if (!grid || !paginationEl) return;

    paginationEl.addEventListener('click', async function (e) {
      const btn = e.target.closest('[data-load-more]');
      if (!btn) return;
      e.preventDefault();
      const url = btn.dataset.loadMore;
      btn.disabled = true;
      btn.textContent = 'Loading…';
      try {
        const res = await fetch(url);
        if (!res.ok) throw new Error(res.status);
        const doc = new DOMParser().parseFromString(await res.text(), 'text/html');
        const newGrid = doc.querySelector('[data-article-grid]');
        if (newGrid) {
          newGrid.querySelectorAll('.article-card').forEach(el => grid.appendChild(el));
        }
        const newPagination = doc.querySelector('[data-blog-pagination]');
        paginationEl.innerHTML = newPagination ? newPagination.innerHTML : '';
      } catch (e) {
        btn.disabled = false;
        btn.textContent = 'Load more';
      }
    });
  }

  /* ------------------------------------------------------------------ */
  /*  Init                                                              */
  /* ------------------------------------------------------------------ */
  function init() {
    registerOverlay('search', '#SearchOverlay');
    registerOverlay('cart', '#CartDrawer');
    registerOverlay('currency', '#CurrencyOverlay');
    registerOverlay('modal', '#ModalOverlay');
    initNavAccordion();
    initMobileDrawer();
    initSearch();
    Cart.init();
    initAccordions();
    initCarousels();
    initProductRecommendations();
    initModals();
    initCurrency();
    initProduct();
    initSlideshow();
    initCollectionFilters();
    initBlogLoadMore();
    initSearchLoadMore();
  }

  if (document.readyState === 'loading') document.addEventListener('DOMContentLoaded', init);
  else init();

  // Public API
  window.Freight = { Cart, overlays };
})();
